/**
 * Authentication Context Provider
 * 
 * This module provides authentication state management for the entire application
 * using React Context API. It handles user authentication status, sign-in, sign-out,
 * and profile information.
 * 
 * @module firebase/AuthContext
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile 
} from 'firebase/auth';
import { auth, googleProvider } from './config';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Authentication Context
 * @type {React.Context}
 */
const AuthContext = createContext();

/**
 * Hook for using authentication context
 * @returns {Object} Authentication context values
 */
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Authentication Provider Component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export function AuthProvider({ children }) {
  // User state and loading status
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * Sign in with Google
   * @returns {Promise<UserCredential>} Firebase user credential
   */
  const signInWithGoogle = async () => {
    try {
      setError('');
      const result = await signInWithPopup(auth, googleProvider);
      // Create user document in Firestore if it doesn't exist
      await createUserDocument(result.user);
      return result;
    } catch (err) {
      setError('Failed to sign in with Google: ' + err.message);
      throw err;
    }
  };

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<UserCredential>} Firebase user credential
   */
  const signInWithEmail = async (email, password) => {
    try {
      setError('');
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Failed to sign in: ' + err.message);
      throw err;
    }
  };

  /**
   * Create a new user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} displayName - User's display name
   * @returns {Promise<UserCredential>} Firebase user credential
   */
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      setError('');
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      await updateProfile(result.user, { displayName });
      
      // Create user document in Firestore
      await createUserDocument(result.user);
      
      return result;
    } catch (err) {
      setError('Failed to create account: ' + err.message);
      throw err;
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise<void>}
   */
  const logout = () => {
    setError('');
    return signOut(auth);
  };

  /**
   * Create a user document in Firestore
   * @param {Object} user - Firebase user object
   * @returns {Promise<void>}
   */
  const createUserDocument = async (user) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);

    // Only create document if it doesn't exist
    if (!snapshot.exists()) {
      const { displayName, email, photoURL } = user;
      
      try {
        await setDoc(userRef, {
          displayName,
          email,
          photoURL,
          createdAt: serverTimestamp(),
          totalCompletions: 0,
          totalPoints: 0,
          highestStreak: 0,
          currentStreak: 0,
          lastPlayedAt: null
        });
      } catch (error) {
        console.error('Error creating user document', error);
      }
    }
  };

  // Subscribe to auth state changes when component mounts
  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    // Unsubscribe when component unmounts
    return unsubscribe;
  }, []);

  // Context value object with all auth methods and state
  const value = {
    currentUser,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    error,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 